import type { ServerListResponse } from '@lumapps/base-api';
import { Template } from '@lumapps/content-templates/types';
import { Group } from '@lumapps/groups/types';
import { TranslatableObject, TranslateObject } from '@lumapps/translations';
import { ContactField, MinimumViableUser, ProfileField } from '@lumapps/user/types';

export type PrefixedUserDirectoryField<S extends string = string> = `lumapps_user_dir_${S}`;

export interface UserDirectoryComponentBoundMap {
    text: string;
    name: string;
}

/** The different field type available in a directory template. */
export enum UserDirectoryTemplateComponentType {
    inputText = 'inputText',
    /** A select field. The type of select (user etc...) is defined by the secondaryType. */
    inputSelect = 'inputSelect',
    /** Metadata field. */
    metadata = 'metadata',
    /** Number field. */
    inputNumber = 'inputNumber',
    /** Date field. */
    inputDate = 'inputDate',
}

/** The available secondary types. */
export enum UserDirectoryComponentSecondaryTypes {
    userPicker = 'userPicker',
}

export interface UserDirectoryComponentProperties {
    /** Icon of the field to display */
    icon?: string;
    /** Whether the field is bound to a user profile field. */
    isBound?: boolean;
    /** The mapping for the bound field. */
    boundMap?: UserDirectoryComponentBoundMap;
    /** The id of the metadata family. Only if type === metadata. */
    metadataId?: string;
    /** Whether the user can edit this field. */
    selfEdit?: boolean;
    /** Whether the field should be displayed as a multiline textarea. */
    textarea?: boolean;
    /** The regex pattern to use to validate fields. */
    validationRegex?: string;
    /** The error label to display when the field is invalid. */
    validationRegexErrorLabel?: TranslateObject;
    /** The secondary type of the component. (ex: inputSelect) */
    secondaryType?: UserDirectoryComponentSecondaryTypes;
    /** Whether the field is listed in the user directory table. */
    displayInList?: boolean;
    /** Whether the field can be used to filter the user directory. Required to filter on this field. */
    displayInFilter?: boolean;
    /** Whether the field spans the full width of the user profile rather than half. */
    fullWidth?: boolean;
    /** List of groups that can edit the fields */
    editFeeds?: Array<Group['id']>;
    /** List of groups that can see the fields */
    seeFeeds?: Array<Group['id']>;
    /** Widget-only: the custom message body. Only if type === 'widget'. */
    content?: TranslateObject;
    /** Widget-only: whether the user can dismiss the custom message. Only if type === 'widget'. */
    isDeletable?: boolean;
}

export interface UserDirectoryTemplateComponent {
    /** The Id of the template component */
    uuid: string;
    /** The type of field. 'widget' is a backend-injected placeholder for the directory's custom message block. */
    type: UserDirectoryTemplateComponentType | 'widget';
    /** The title of the field. */
    title: TranslateObject;
    /** The properties of the field. */
    properties?: UserDirectoryComponentProperties;
}

/**
 * Defines the structure for the properties object, used for
 * configuration related to a user interface.
 */
export interface UserDirectoryProperties {
    /** Controls if users are permitted to update their own profiles. */
    allowProfileUpdate: boolean;
    /** Determines whether filtering options are shown in the UI. */
    displayFilters: boolean;
    /** Specifies if the organizational chart (org chart) should be displayed. */
    displayOrganizationChart: boolean;
    /** Controls whether user profiles  are visible. */
    displayUserProfile: boolean;
    /** The unique identifier (likely a UUID) for the manager relationship pattern or template. */
    managerPatternId: string;
    /** Stores an error message */
    publicationError: string | null;
    /** An array of user groups identifiers */
    userGroups: string[];
}

/** HelpBlock interface for widget help message  */
export interface HelpBlock {
    widgetId?: string;
    /** title of widget help message */
    title?: TranslatableObject;
    /** description of widget help message */
    body?: TranslatableObject;
    /** whether the widget help message could be discardable or not */
    isDiscardable: boolean;
}

export interface UserDirectory extends MinimumViableUser {
    /** Id of the user directory. */
    id: string;
    /** Is the default user directory. */
    isDefaultUserDirectory?: boolean;
    /** Translatable object. Name of the user directory. */
    title: Record<string, string>;
    /** URL slug of the directory (translatable). */
    slug?: Record<string, string>;
    /** Group IDs controlling who can see this directory. */
    feedKeys?: string[];
    /** Optional help/custom message block shown at the top of the directory. */
    helpBlock?: HelpBlock;
    /** Timestamp of the last update — required by the backend for concurrency control when saving. */
    updatedAt?: string;
    /** Template of the user directory fields. */
    template: Template<UserDirectoryTemplateComponent>;
    /** properties of the user directory fields. */
    properties: UserDirectoryProperties;
    /** An array of additional, often custom, profile fields. */
    profileFields: ProfileField[];

    /** An array of contact information fields (e.g., phone, secondary email). */
    contactFields: ContactField[];

    /** TODO: There is more props, please add it when necessary. */
}

export type GetUsersByDirectoryResponse = ServerListResponse<UserDirectory>;

/**
 * Represents all the fields it contains (e.g., "Name", "Email", "Phone Number").
 * defines which fields should be displayed in user card and filters
 */
export interface FieldDefinitions {
    iconClass: string;
    /** The id of the field. */
    id: string;
    /** Whether the field could be displayed in the list */
    isHighlighted: boolean;
    /** Metadata library id  */
    metadataLibraryId: string;
    /** component title  */
    componentTitle: string;
    /** order field displaying  */
    order: number;
    /** Whether the field could be displayed in the filters */
    searchable: boolean;
    /** The title of the field. */
    title: TranslateObject;
    /** The type of field. */
    type: UserDirectoryTemplateComponentType | string;
    updatedAt: string;
}

/**
 * Defines the complete configuration for a single directory.
 *  which contains info of fields in user cards, filters, helpBlock, groups list displayed on groupPicker filter , directory information
 */
export interface DirectoryConfiguration {
    /**
     * The unique identifier for this directory configuration.
     * @type {string}
     */
    id: string;

    /**
     * the date of user directory creation
     * @type {string}
     */
    createdAt: string;

    /**
     * An optional, multilingual description for the directory.
     * @type {TranslateObject | undefined}
     */
    description?: TranslateObject;

    /**
     * The required, multilingual title for the directory. This is what
     * users will actually see.
     * @type {TranslateObject}
     */
    title: TranslateObject;

    /**
     * The heart of the directory
     * all the fields it contains (e.g., "Name", "Email", "Phone Number").
     * defines which fields should be displayed in user card and filters
     * @type {FieldDefinitions[]}
     */
    fieldDefinitions: FieldDefinitions[];

    /**
     * An object containing instructional text to help users.
     * @type {HelpBlock}
     */
    helpBlock: HelpBlock;

    /**
     * this configuration was modified.
     */
    updatedAt: string;

    /**
     * An array of group IDs. This controls which user groups
     */
    displayGroupIds: string[];
}
